import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

const EFFECTIVE_DATE = "April 1, 2026";
const COMPANY_NAME = "A3 Assurance Inc.";
const CONTACT_EMAIL = "privacy@a3assurance.com";

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" fontWeight={700} gutterBottom>
      {title}
    </Typography>
    {children}
  </Box>
);

const P = ({ children }) => (
  <Typography variant="body1" sx={{ mb: 1.5, color: "text.secondary", lineHeight: 1.8 }}>
    {children}
  </Typography>
);

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ backgroundColor: "#f9fafb", minHeight: "100vh", py: 6 }}>
      {/* Nav bar */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#fff",
          borderBottom: "1px solid #e5e7eb",
          px: 4,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="h6"
          fontWeight={800}
          sx={{ color: "#1e40af", cursor: "pointer" }}
          onClick={() => navigate("/")}
        >
          SFM
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" size="small" onClick={() => navigate("/login")}>
            Log In
          </Button>
          <Button variant="contained" size="small" onClick={() => navigate("/signup")}>
            Start Free Trial
          </Button>
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ backgroundColor: "#fff", borderRadius: 2, p: { xs: 3, md: 6 }, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Effective Date: {EFFECTIVE_DATE}
          </Typography>
          <Divider sx={{ mb: 4 }} />

          <P>
            {COMPANY_NAME} ("A3 Assurance," "we," "us," or "our") is committed to protecting
            your personal information. This Privacy Policy explains how we collect, use, disclose,
            and safeguard data in connection with our Shop Floor Management (SFM) platform.
          </P>

          <Section title="1. Information We Collect">
            <P><strong>Account Information:</strong> When you register, we collect your name, email address, company name, and a hashed password.</P>
            <P><strong>Customer Data:</strong> Data you upload or generate through SFM — including heat records, weld logs, NCR reports, inspection records, purchase orders, and shipping data — is stored on our servers on your behalf. You own this data.</P>
            <P><strong>Usage Data:</strong> We automatically collect log data, including IP addresses, browser type, pages visited, timestamps, and feature usage, to operate and improve the Service.</P>
            <P><strong>Payment Information:</strong> Billing is handled by Stripe, Inc. A3 Assurance does not store credit card numbers. We receive only a token reference and last-four-digits summary from Stripe.</P>
            <P><strong>Communications:</strong> If you contact us for support, we retain the content of your messages to resolve your issue.</P>
          </Section>

          <Section title="2. How We Use Your Information">
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f3f4f6" }}>
                    <TableCell><strong>Purpose</strong></TableCell>
                    <TableCell><strong>Legal Basis</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    ["Provide and maintain the Service", "Contract performance"],
                    ["Process payments and manage subscriptions", "Contract performance"],
                    ["Send transactional emails (receipts, trial expiry)", "Contract performance"],
                    ["Improve platform features and fix bugs", "Legitimate interest"],
                    ["Comply with legal obligations", "Legal obligation"],
                    ["Send product updates (opt-out available)", "Legitimate interest"],
                  ].map(([purpose, basis]) => (
                    <TableRow key={purpose}>
                      <TableCell>{purpose}</TableCell>
                      <TableCell>{basis}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>

          <Section title="3. Data Sharing and Disclosure">
            <P>We do not sell your personal information. We share data only with:</P>
            <P><strong>Service Providers:</strong> Stripe (payments), DigitalOcean (hosting), and email delivery providers. These partners process data only on our instructions under confidentiality agreements.</P>
            <P><strong>Legal Requirements:</strong> We may disclose information if required by law, court order, or to protect the rights, property, or safety of A3 Assurance, our customers, or others.</P>
            <P><strong>Business Transfers:</strong> In connection with a merger, acquisition, or asset sale, Customer Data will be transferred subject to the same privacy protections. We will provide notice before transfer.</P>
          </Section>

          <Section title="4. Data Retention">
            <P>
              We retain your Customer Data for the duration of your Subscription and for 90 days
              following termination, during which you may export your data. After 90 days,
              Customer Data is permanently deleted from our systems. Log data and analytics are
              retained for up to 24 months. Billing records are retained as required by law.
            </P>
          </Section>

          <Section title="5. Security">
            <P>
              A3 Assurance implements the following controls to protect your data: TLS encryption
              in transit; AES-256 encryption at rest for database volumes; access controls with
              role-based permissions; regular security updates; audit logging of all data access
              and modifications within the platform.
            </P>
            <P>
              No method of transmission or storage is 100% secure. If we become aware of a breach
              affecting your data, we will notify you within 72 hours as required by applicable law.
            </P>
          </Section>

          <Section title="6. Cookies and Tracking">
            <P>
              SFM uses cookies and local storage to maintain your login session. We do not use
              third-party advertising cookies. We use minimal analytics to understand feature usage.
              You can disable cookies in your browser settings, though this will prevent login.
            </P>
          </Section>

          <Section title="7. Your Rights">
            <P>Depending on your jurisdiction, you may have the right to:</P>
            <P>
              <strong>Access</strong> — Request a copy of the personal data we hold about you.<br />
              <strong>Correction</strong> — Request correction of inaccurate information.<br />
              <strong>Deletion</strong> — Request deletion of your personal data (subject to legal retention obligations).<br />
              <strong>Portability</strong> — Export your Customer Data in JSON format via the account settings page.<br />
              <strong>Opt-out</strong> — Unsubscribe from marketing emails at any time via the link in any email we send.
            </P>
            <P>
              To exercise your rights, email us at{" "}
              <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>. We will respond
              within 30 days.
            </P>
          </Section>

          <Section title="8. Canadian Privacy Law (PIPEDA / Law 25)">
            <P>
              A3 Assurance is a Canadian company subject to the Personal Information Protection
              and Electronic Documents Act (PIPEDA) and applicable provincial privacy legislation.
              We collect only the minimum personal information necessary for the stated purposes
              and obtain consent at the point of collection.
            </P>
          </Section>

          <Section title="9. International Data Transfers">
            <P>
              Customer Data is hosted on servers located in Canada and the United States (DigitalOcean).
              By using the Service, you consent to your data being processed in these jurisdictions.
              We ensure appropriate safeguards are in place for any cross-border transfers.
            </P>
          </Section>

          <Section title="10. Children's Privacy">
            <P>
              The Service is not directed to individuals under 18 years of age. We do not knowingly
              collect personal information from minors. If you believe a minor has provided us with
              personal data, please contact us immediately.
            </P>
          </Section>

          <Section title="11. Changes to This Policy">
            <P>
              We may update this Privacy Policy periodically. We will notify you by email and
              in-app notice at least 30 days before material changes take effect. Continued use
              of the Service after the effective date constitutes acceptance of the updated policy.
            </P>
          </Section>

          <Section title="12. Contact Us">
            <P>
              Privacy inquiries:{" "}
              <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>
            </P>
            <P>
              {COMPANY_NAME}<br />
              Toronto, Ontario, Canada
            </P>
          </Section>

          <Divider sx={{ my: 4 }} />
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button variant="outlined" onClick={() => navigate("/terms")}>
              Terms of Service
            </Button>
            <Button variant="contained" onClick={() => navigate("/signup")}>
              Start Free Trial
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
